import { useEffect, useMemo, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { httpGET } from '../../../../services/http';
import { setPlaneComputing, setPlaneResults } from '../../../../store/reducers/measurementPlanes.reducer.ts';
import { setMeasurementVisualizationType } from '../../../../store/reducers/workspace.reducer.ts';
import { getNorms } from '../../../../store/selectors/user.selector.ts';
import { selectLampWireframeMap } from '../../../../store/selectors/workspace.selector.ts';
import type { PresignedUrlResponse } from '../../../../types/file';
import type { IesCandelaGrid } from '../../../../types/lampWireframes';
import type { Light } from '../../../../types/light';
import { MEASUREMENT_VISUALIZATION_FIELD_MAP, type MeasurementVisualizationType } from '../../../../types/view';
import { fetchLdcAndBuildWireframeWithGrid } from '../../../../util/ldc';
import {
  type LuminaireSource,
  buildCalcInput,
  buildCalcLuminaires,
  fieldsFromTargets,
  normToExpectedValues,
  resolveCalcRotationRadians,
} from '../../../../utils/measurementCalculation/adapter';
import { buildOcclusionScene } from '../../../../utils/measurementCalculation/occlusion.ts';
import type { CalcLayer, CalcPoint, MeasurementField } from '../../../../utils/measurementCalculation/types';
import { POINT_LIFT_M, drapeFromCamera, drapePlaneGrid } from '../../../../utils/measurementPlane/drape.ts';
import { frameToWorld } from '../../../../utils/measurementPlane/frame.ts';
import { buildPlaneSurfaceGeometry } from '../../../../utils/measurementPlane/geometry.ts';
import { samplePlaneGrid } from '../../../../utils/measurementPlane/sampler.ts';
import { measurementWorkerManager } from '../../../../workers/measurementWorkerManager';
import { useIlluminatedGeometry } from '../useIlluminatedGeometry';

const ML_MAINTENANCE_FACTOR = 0.9;
const DEBOUNCE_MS = 350;

/**
 * Computes frontend measurement values for every visible measurement plane and
 * stores them under `measurementPlanes.results`. Resolves each lamp's IES grid the
 * same way the main FrontendMeasurementRunner does (wireframe → catalogue variant
 * → fetch by iesFileId), so it works even when wireframes aren't loaded.
 * Step 1: horizontal planes, E only, no occlusion.
 */
export function MeasurementPlaneRunner(): null {
  const dispatch = useAppDispatch();
  const planes = useAppSelector((s) => s.measurementPlanes.planes);
  const lamps = useAppSelector((s) => s.workspace.lamps);
  const project = useAppSelector((s) => s.workspace.project);
  const gridSpacing = useAppSelector((s) => s.workspace.measurementGridSpacing);
  const maxApexAngleDegrees = useAppSelector((s) => s.workspace.maxApexAngleDegrees);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const wireframeMap = useAppSelector(selectLampWireframeMap);
  const thirdPartyWireframes = useAppSelector((s) => s.workspace.lampWireframes.thirdPartyWireframes);
  const norms = useAppSelector(getNorms);
  const normsRef = useRef(norms);
  normsRef.current = norms;
  const vizType = useAppSelector((s) => s.workspace.measurementVisualizationType);
  const vizRef = useRef(vizType);
  vizRef.current = vizType;
  const pointsView = useAppSelector((s) => s.measurementPlanes.pointsView);
  const results = useAppSelector((s) => s.measurementPlanes.results);
  // Imported model geometry → occluders, so occlusion matches the main frontend calc.
  const extractGeometry = useIlluminatedGeometry();
  const extractRef = useRef(extractGeometry);
  extractRef.current = extractGeometry;

  // Async IES grid caches (mirror FrontendMeasurementRunner).
  const iesGridCache = useRef<Map<number, IesCandelaGrid>>(new Map());
  const variantGridCache = useRef<Map<number, IesCandelaGrid | null>>(new Map());
  const productCache = useRef<Map<number, { variants?: Array<{ id: string | number; ldcFileUrl?: string }> } | null>>(
    new Map(),
  );

  const lights: Light[] = useMemo(
    () => project?.versions?.find((v) => v.id === project?.activeVersionId)?.lights ?? [],
    [project],
  );

  const signature = useMemo(
    () =>
      JSON.stringify({
        p: planes.map((p) => ({
          id: p.id,
          v: p.visible,
          h: p.height,
          poly: p.polygon,
          s: p.spacing,
          e: p.exclusions,
          n: p.normId,
        })),
        l: lamps.map((l) => ({
          u: l.uuid,
          n: l.name,
          p: l.position,
          d: l.distanceToGround,
          r: l.rotationRadians,
          i: l.iesFileId,
          pr: l.productId,
          va: l.variantId,
        })),
        g: gridSpacing,
        a: maxApexAngleDegrees,
        // Re-drape once the model's layer meshes load (geometry isn't in `signature`).
        m: modelLayers.map((l) => l.threeJsName),
      }),
    [planes, lamps, gridSpacing, maxApexAngleDegrees, modelLayers],
  );

  useEffect(() => {
    let cancelled = false;

    const resolveGrid = async (light: Light): Promise<IesCandelaGrid | undefined> => {
      const fromWire = wireframeMap.get(light.uuid)?.wireframe?.iesGrid;
      if (fromWire) return fromWire;

      if (light.productId != null && light.variantId != null) {
        const cached = variantGridCache.current.get(light.variantId);
        if (cached !== undefined) {
          if (cached) return cached;
        } else {
          try {
            let product = productCache.current.get(light.productId);
            if (product === undefined) {
              const resp = await httpGET(`/catalogue/products/${light.productId}`);
              product = (resp.data as { variants?: Array<{ id: string | number; ldcFileUrl?: string }> }) ?? null;
              productCache.current.set(light.productId, product);
            }
            const variant = product?.variants?.find(
              (v) => v.id === light.variantId || String(v.id) === String(light.variantId),
            );
            if (variant?.ldcFileUrl) {
              const { grid } = await fetchLdcAndBuildWireframeWithGrid(variant.ldcFileUrl);
              variantGridCache.current.set(light.variantId, grid);
              return grid;
            }
          } catch (err) {
            console.warn('[measurement-plane] failed to load variant LDC', err);
          }
          variantGridCache.current.set(light.variantId, null);
        }
      }

      if (light.iesFileId != null) {
        let grid = iesGridCache.current.get(light.iesFileId);
        if (!grid) {
          try {
            const resp = await httpGET(`/files/${light.iesFileId}/presigned-url/download`);
            const url = (resp.data as PresignedUrlResponse).url;
            const res = await fetchLdcAndBuildWireframeWithGrid(url);
            grid = res.grid;
            iesGridCache.current.set(light.iesFileId, grid);
          } catch (err) {
            console.warn('[measurement-plane] failed to load IES for', light.name, err);
          }
        }
        if (grid) return grid;
      }
      return undefined;
    };

    const run = async () => {
      const visible = planes.filter((p) => p.visible && p.polygon.length >= 3);
      if (visible.length === 0) {
        dispatch(setPlaneResults([]));
        dispatch(setPlaneComputing(false));
        return;
      }
      dispatch(setPlaneComputing(true)); // shows "Calculating…" in the sidebar

      // Prefer live workspace lamps (carry unsaved positions/product swaps).
      const calcLights: Light[] = lamps.length
        ? lamps.map((lamp) => {
            const versionLight = lights.find((l) => l.uuid === lamp.uuid);
            return {
              ...(versionLight ?? {}),
              uuid: lamp.uuid,
              name: lamp.name,
              positionX: lamp.position[0],
              positionY: lamp.position[1],
              positionZ: lamp.position[2],
              distanceToGround:
                lamp.distanceToGround > 0 ? lamp.distanceToGround : (versionLight?.distanceToGround ?? 8),
              iesFileId: lamp.iesFileId ?? versionLight?.iesFileId,
              productId: lamp.productId ?? versionLight?.productId,
              variantId: lamp.variantId ?? versionLight?.variantId,
              rotationRadians: resolveCalcRotationRadians(
                lamp.uuid,
                lamp.rotationRadians ?? versionLight?.rotationRadians,
                wireframeMap,
                thirdPartyWireframes,
              ),
            } as Light;
          })
        : lights;

      const sources: LuminaireSource[] = [];
      for (const light of calcLights) {
        sources.push({ light, iesGrid: await resolveGrid(light) });
      }
      if (cancelled) return;

      const luminaires = buildCalcLuminaires(sources);
      if (luminaires.length === 0) {
        dispatch(setPlaneResults([]));
        dispatch(setPlaneComputing(false));
        return;
      }

      // Imported model geometry → occluders AND the surface we drape grid points onto.
      const occluderGeometries = Array.from(extractRef.current().values());
      const drapeScene = buildOcclusionScene(occluderGeometries);

      // Coarsest per-surface spacing drives the engine's boundary mask so no surface
      // gets all its points trimmed as "edge" (the mask groups all points at one spacing).
      let boundarySpacing = 0;

      const layers: CalcLayer[] = visible
        .map((p) => {
          const custom = p.customTargets?.length ? p.customTargets : undefined;
          const norm = !custom && p.normId != null ? normsRef.current.find((n) => n.id === p.normId) : undefined;
          const allTargets = custom ?? (norm ? normToExpectedValues(norm) : []);
          const vertical = !!p.frame;
          // Vertical (façade) surfaces: only the planar illuminance E is meaningful
          // (Ev/Esc/L are road-direction quantities). Horizontal: full norm field set.
          const targets = vertical ? allTargets.filter((t) => t.function === 'E') : allTargets;
          const fields: MeasurementField[] = vertical
            ? ['E']
            : allTargets.length
              ? Array.from(new Set<MeasurementField>([...fieldsFromTargets(allTargets), 'E']))
              : ['E'];

          // Per-surface measurement-point spacing (falls back to the global setting).
          const spacing = p.spacing && p.spacing > 0 ? p.spacing : gridSpacing;
          if (spacing > boundarySpacing) boundarySpacing = spacing;
          const grid = samplePlaneGrid(p, spacing);
          let points: CalcPoint[];
          let geometry;
          if (vertical) {
            const f = p.frame!;
            if (drapeScene && p.cameraPos) {
              // Façade: raycast each grid point FROM the draw-time camera onto the model, so
              // points land on the exact surface the user saw (following protrusions/recesses)
              // and never inside the building. Occlusion (below) then correctly shadows them.
              points = drapeFromCamera(drapeScene, p, grid, p.cameraPos);
            } else {
              // Fallback (no model / pre-camera surface): place on the drawn wall plane.
              points = grid.map(([u, v]): CalcPoint => {
                const w = frameToWorld([u, v], f);
                return {
                  position: [
                    w[0] + f.normal[0] * POINT_LIFT_M,
                    w[1] + f.normal[1] * POINT_LIFT_M,
                    w[2] + f.normal[2] * POINT_LIFT_M,
                  ],
                  normal: f.normal,
                };
              });
            }
            geometry = { positions: [] as number[] };
          } else if (drapeScene) {
            // Horizontal: DRAPE the grid onto the real model surface so points follow the
            // terrain instead of a flat plane (coords with no surface beneath are dropped).
            points = drapePlaneGrid(drapeScene, grid);
            // Footprint for road-direction PCA, placed BELOW every draped point so the
            // (flat) footprint never sits between a lamp and a point in the occlusion test.
            let minZ = Infinity;
            for (const pt of points) if (pt.position[2] < minZ) minZ = pt.position[2];
            geometry = Number.isFinite(minZ)
              ? buildPlaneSurfaceGeometry(p.polygon, minZ - 0.5)
              : { positions: [] as number[] };
          } else {
            // No model loaded yet → flat grid at the drawn height (auto from the surface).
            points = grid.map(([u, v]): CalcPoint => ({ position: [u, v, p.height], normal: [0, 0, 1] }));
            geometry = buildPlaneSurfaceGeometry(p.polygon, p.height);
          }

          return {
            uuid: p.id,
            name: p.name,
            normId: p.normId ?? 0,
            geometry,
            points,
            targets,
            fields,
            // Show the full drawn grid — occluded/unreachable points read 0 (visible shadowing).
            keepAllPoints: true,
          };
        })
        .filter((l) => (l.points?.length ?? 0) > 0);

      if (layers.length === 0) {
        dispatch(setPlaneResults([]));
        dispatch(setPlaneComputing(false));
        return;
      }

      // Diagnostic: per surface — orientation, point count, and a sample point.
      console.info(
        '[measurement-plane] computing',
        { luminaires: luminaires.length },
        layers.map((l) => {
          const p = visible.find((vp) => vp.id === l.uuid);
          return {
            name: l.name,
            type: p?.frame ? 'vertical' : 'horizontal',
            points: l.points?.length ?? 0,
            sample: l.points?.[0] ? { pos: l.points[0].position, normal: l.points[0].normal } : null,
          };
        }),
      );

      const input = buildCalcInput(luminaires, layers, {
        maintenanceFactor: ML_MAINTENANCE_FACTOR,
        maxApexAngleDegrees,
        gridSpacingM: boundarySpacing > 0 ? boundarySpacing : gridSpacing,
        // General planar illuminance (cosθ/d²) so oriented (vertical) planes are correct;
        // numerically equivalent to the road E on horizontal planes.
        planarIlluminance: true,
        // The user drew this exact area — keep every point (no edge "ignored" trimming),
        // so values match the model's measurement points at the same locations.
        boundaryTrim: false,
        singleLampTrim: false,
      });
      // Occlude against the imported model (same as the main frontend calc). occlusion
      // defaults to true in the engine, matching FrontendMeasurementRunner.
      input.occluders = occluderGeometries;

      try {
        const res = await measurementWorkerManager.calculate(input).promise;
        if (cancelled) return; // a newer run owns the computing flag now
        // Diagnostic: report computed E per surface (avg over the points).
        console.info(
          '[measurement-plane] results',
          res.map((r) => {
            const e = r.actualValues.find((a) => a.function === 'E');
            return { name: r.name, E: e ? e.value : null };
          }),
        );
        dispatch(setPlaneResults(res));
        dispatch(setPlaneComputing(false));
        if (res.length && vizRef.current == null) {
          dispatch(setMeasurementVisualizationType('horizontalIlluminance'));
        }
      } catch (err) {
        if (!cancelled) dispatch(setPlaneComputing(false));
        console.warn('[measurement-plane] calc failed', err);
      }
    };

    const timer = setTimeout(run, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, wireframeMap]);

  // When showing surface points, make sure the colour quantity is one the SURFACE data
  // actually has — otherwise the legend can't build a scale (no colour bar) and points
  // render uncoloured. (The global type may be set to a model-only quantity or tamanScore.)
  useEffect(() => {
    if (pointsView !== 'surface' || results.length === 0) return;
    const fns = new Set<string>();
    for (const r of results) for (const a of r.actualValues) if (a.value != null) fns.add(a.function);
    if (fns.size === 0) return;
    const curField = vizType && vizType !== 'tamanScore' ? MEASUREMENT_VISUALIZATION_FIELD_MAP[vizType] : null;
    if (curField && fns.has(curField)) return; // current quantity is valid for the surface
    const preferred: [string, MeasurementVisualizationType][] = [
      ['E', 'horizontalIlluminance'],
      ['L', 'luminance'],
      ['Ev', 'verticalIlluminance'],
      ['Esc', 'semiCylindricalIlluminance'],
      ['Ehs', 'hemisphericalIlluminance'],
    ];
    const next = preferred.find(([fn]) => fns.has(fn))?.[1] ?? 'horizontalIlluminance';
    dispatch(setMeasurementVisualizationType(next));
  }, [pointsView, results, vizType, dispatch]);

  return null;
}
